const SEO_DEFAULT_LOCALE = 'zh';

const SEO_SUPPORTED_LOCALES = ['zh', 'en'];

const HREFLANG_MAP = {
  zh: 'zh-CN',
  en: 'en-US'
};

const CONTENT_LANGUAGE_MAP = {
  zh: 'zh-CN',
  en: 'en-US'
};

const DEFAULT_CONTENT_LANGUAGE = 'zh-CN';

const // 分页路由（如 /blog/page/2）不在 INDEXABLE_BASE_PATHS 中，其 basePath 通过 evaluateRouteIndexability 被自然排除在 sitemap 之外。/blog/ 前缀特例仅匹配文章详情，不会误匹配分页路由。
INDEXABLE_BASE_PATHS = [
  '/',
  '/about',
  '/privacy',
  '/terms',
  '/blog',
  '/translate-pdf',
  '/translate-office',
  '/translate-ebook',
  '/translate-subtitle'
];

const EXCLUDED_EXACT_BASE_PATHS = new Set([
  '/pay',
  '/pay-success',
  '/wechat-callback'
]);

const EXCLUDED_PREFIX_BASE_PATHS = ['/my-page'];

const XML_ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&apos;'
};

function escapeXml(value) {
  return String(value).replace(/[&<>"']/g, (char) => XML_ESCAPE_MAP[char] ?? char);
}

function uniqueStrings(items) {
  return [...new Set(items)];
}

function mergeSitemapCandidateRoutes(baseRoutes = [], extraRoutes = []) {
  const merged = [];
  const seen = new Set();

  for (const route of [...baseRoutes, ...extraRoutes]) {
    const normalized = normalizePath(route);
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    merged.push(normalized);
  }

  return merged;
}

function normalizePath(path) {
  const raw = String(path ?? '').trim();
  const withoutQuery = raw.split('?')[0]?.split('#')[0] ?? '';
  if (withoutQuery.length === 0) return '/';

  let normalized = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  normalized = normalized.replace(/\/{2,}/g, '/');

  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized || '/';
}

function parseLocalePath(path, options = {}) {
  const defaultLocale = options.defaultLocale ?? SEO_DEFAULT_LOCALE;
  const locales = options.locales ?? SEO_SUPPORTED_LOCALES;
  const normalized = normalizePath(path);

  for (const locale of locales) {
    const prefix = `/${locale}`;
    if (normalized === prefix) {
      return { locale, basePath: '/' };
    }
    if (normalized.startsWith(`${prefix}/`)) {
      const basePath = normalized.slice(prefix.length) || '/';
      return { locale, basePath: normalizePath(basePath) };
    }
  }

  return { locale: defaultLocale, basePath: normalized };
}

function toLocalizedPath(basePath, locale, options = {}) {
  const defaultLocale = options.defaultLocale ?? SEO_DEFAULT_LOCALE;
  const normalizedBase = normalizePath(basePath);

  if (locale === defaultLocale) {
    return normalizedBase;
  }
  if (normalizedBase === '/') {
    return `/${locale}`;
  }

  return `/${locale}${normalizedBase}`;
}

function isExcludedBasePath(basePath) {
  if (EXCLUDED_EXACT_BASE_PATHS.has(basePath)) {
    return true;
  }

  return EXCLUDED_PREFIX_BASE_PATHS.some((prefix) => basePath === prefix || basePath.startsWith(`${prefix}/`));
}

function ensureValidSiteUrl(siteUrl) {
  const raw = String(siteUrl ?? '').trim();
  if (raw.length === 0) {
    throw new Error('NUXT_PUBLIC_SITE_URL is required and must be a valid absolute URL');
  }

  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error('NUXT_PUBLIC_SITE_URL is required and must be a valid absolute URL');
  }

  if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.host) {
    throw new Error('NUXT_PUBLIC_SITE_URL is required and must be a valid absolute URL');
  }

  return parsed.origin;
}

function canonicalizeUrl(input) {
  const origin = ensureValidSiteUrl(input?.siteUrl);
  const path = normalizePath(input?.path ?? '/');
  if (path === '/') {
    return `${origin}/`;
  }
  return `${origin}${path}`;
}

function evaluateRouteIndexability(path, options = {}) {
  const defaultLocale = options.defaultLocale ?? SEO_DEFAULT_LOCALE;
  const locales = options.locales ?? SEO_SUPPORTED_LOCALES;
  const normalized = normalizePath(path);
  const { basePath } = parseLocalePath(normalized, { defaultLocale, locales });
  const publishedBlogRoutesSet =
    options.publishedBlogRoutesSet instanceof Set
      ? options.publishedBlogRoutesSet
      : new Set((options.publishedBlogRoutes ?? []).map((item) => normalizePath(item)));
  const hasPublishedBlogHint =
    options.publishedBlogRoutesSet instanceof Set || Array.isArray(options.publishedBlogRoutes);

  if (isExcludedBasePath(basePath)) {
    return { shouldIndex: false, inSitemap: false, normalizedPath: normalized, basePath };
  }

  if (INDEXABLE_BASE_PATHS.includes(basePath)) {
    return { shouldIndex: true, inSitemap: true, normalizedPath: normalized, basePath };
  }

  if (basePath.startsWith('/blog/')) {
    const isPublishedBlogRoute = hasPublishedBlogHint ? publishedBlogRoutesSet.has(normalized) : true;
    return {
      shouldIndex: isPublishedBlogRoute,
      inSitemap: isPublishedBlogRoute,
      normalizedPath: normalized,
      basePath
    };
  }

  return { shouldIndex: false, inSitemap: false, normalizedPath: normalized, basePath };
}

function buildAlternateLinks(input) {
  const origin = ensureValidSiteUrl(input?.siteUrl);
  const defaultLocale = input?.defaultLocale ?? SEO_DEFAULT_LOCALE;
  const availableLocales = uniqueStrings((input?.availableLocales ?? SEO_SUPPORTED_LOCALES).filter(Boolean));
  const normalizedPath = normalizePath(input?.path ?? '/');
  const { basePath } = parseLocalePath(normalizedPath, {
    defaultLocale,
    locales: SEO_SUPPORTED_LOCALES
  });

  const links = [];
  for (const locale of availableLocales) {
    if (!SEO_SUPPORTED_LOCALES.includes(locale)) continue;
    const href = canonicalizeUrl({ siteUrl: origin, path: toLocalizedPath(basePath, locale, { defaultLocale }) });
    links.push({
      hreflang: HREFLANG_MAP[locale] ?? locale,
      href
    });
  }

  if (availableLocales.length > 1 && availableLocales.includes(defaultLocale)) {
    links.push({
      hreflang: 'x-default',
      href: canonicalizeUrl({
        siteUrl: origin,
        path: toLocalizedPath(basePath, defaultLocale, { defaultLocale })
      })
    });
  }

  return links;
}

function resolveContentLanguageFromPath(path, options = {}) {
  const locales = options.locales ?? SEO_SUPPORTED_LOCALES;
  const defaultLocale = options.defaultLocale ?? SEO_DEFAULT_LOCALE;
  const { locale } = parseLocalePath(path, { defaultLocale, locales });
  return CONTENT_LANGUAGE_MAP[locale] ?? DEFAULT_CONTENT_LANGUAGE;
}

function buildDefaultSitemapRoutes(options = {}) {
  const locales = options.locales ?? SEO_SUPPORTED_LOCALES;
  const defaultLocale = options.defaultLocale ?? SEO_DEFAULT_LOCALE;
  const routes = [];

  for (const basePath of INDEXABLE_BASE_PATHS) {
    for (const locale of locales) {
      if (!SEO_SUPPORTED_LOCALES.includes(locale)) continue;
      routes.push(toLocalizedPath(basePath, locale, { defaultLocale }));
    }
  }

  return uniqueStrings(routes);
}

const SITEMAP_CANDIDATE_ROUTES = buildDefaultSitemapRoutes();

function buildSitemapXml(input) {
  const origin = ensureValidSiteUrl(input?.siteUrl);
  const baseCandidates = Array.isArray(input?.routes) ? input.routes : SITEMAP_CANDIDATE_ROUTES;
  const publishedBlogRoutes = Array.isArray(input?.publishedBlogRoutes) ? input.publishedBlogRoutes : [];
  const candidates = mergeSitemapCandidateRoutes(baseCandidates, publishedBlogRoutes);
  const publishedBlogRoutesSet = new Set(publishedBlogRoutes.map((item) => normalizePath(item)));

  const urls = [];
  const seen = new Set();

  for (const candidate of candidates) {
    const normalized = normalizePath(candidate);
    const decision = evaluateRouteIndexability(normalized, { publishedBlogRoutesSet });
    if (!decision.inSitemap) continue;

    const absoluteUrl = canonicalizeUrl({ siteUrl: origin, path: normalized });
    if (seen.has(absoluteUrl)) continue;
    seen.add(absoluteUrl);
    urls.push(absoluteUrl);
  }

  const rows = urls.map((url) => `  <url><loc>${escapeXml(url)}</loc></url>`).join('\n');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    rows,
    '</urlset>',
    ''
  ].join('\n');
}

function buildRobotsTxt(siteUrl) {
  const origin = ensureValidSiteUrl(siteUrl);
  return `User-Agent: *\nAllow: /\nSitemap: ${origin}/sitemap.xml\n`;
}

export {
  SEO_DEFAULT_LOCALE,
  SEO_SUPPORTED_LOCALES,
  INDEXABLE_BASE_PATHS,
  SITEMAP_CANDIDATE_ROUTES,
  buildAlternateLinks,
  buildDefaultSitemapRoutes,
  buildRobotsTxt,
  buildSitemapXml,
  canonicalizeUrl,
  ensureValidSiteUrl,
  evaluateRouteIndexability,
  mergeSitemapCandidateRoutes,
  normalizePath,
  parseLocalePath,
  resolveContentLanguageFromPath,
  toLocalizedPath
};
